import axios from 'axios'
import { getLoginUrl } from './authConfig'

const api = axios.create({
    baseURL: `${process.env.REACT_APP_API_URL}`,
    timeout: 5000000,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
})

const apiGet = axios.create({
    baseURL: `${process.env.REACT_APP_API_URL}`,
    timeout: 5000000,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
})

let isRefreshing = false
let failedQueue: { resolve: (token: string) => void; reject: (err: any) => void }[] = []

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error)
        } else {
            prom.resolve(token!)
        }
    })
    failedQueue = []
}

// Exported for helpers/devSession.ts, which mints the first access token of a
// local dev session from a refresh token the same way the 401 path does.
export const refreshAccessToken = async (): Promise<string> => {
    const refreshToken = window.localStorage.getItem('refresh')
    if (!refreshToken) throw new Error('No refresh token available')

    const params = new URLSearchParams()
    params.append('grant_type', 'refresh_token')
    params.append('client_id', `${process.env.REACT_APP_COGNITO_CLIENT_ID}`)
    params.append('refresh_token', refreshToken)

    const response = await axios.post(`${process.env.REACT_APP_OAUTH}`, params, {
        headers: {
            'content-type': 'application/x-www-form-urlencoded',
            Authorization: `Basic ${process.env.REACT_APP_COGINTO_AUTHORIZATION}`,
        },
    })

    const { access_token, id_token } = response.data
    window.localStorage.setItem('access', access_token)
    window.localStorage.setItem('id', id_token)

    // Stamp activity on silent refresh too. The refresh path never goes through
    // the hosted-UI /auth flow, so without this `last_login` would only ever
    // reflect full sign-ins — an actively-used session (token refreshed roughly
    // hourly) would show a stale date for weeks. Fire-and-forget against /init
    // (which records the login server-side); never block the retried request or
    // surface errors from it.
    try {
        axios
            .post(
                `${process.env.REACT_APP_API_URL}/init`,
                {},
                { headers: { Authorization: access_token, 'Content-Type': 'application/json' } },
            )
            .catch(() => {})
    } catch {
        // best-effort only
    }

    return access_token
}

api.interceptors.request.use(
    async (config: any) => {
        const storedAccessToken = window.localStorage.getItem('access')
        if (!storedAccessToken) {
            console.log('no access token for session')
            window.location.href = getLoginUrl()
            return
        }
        if (storedAccessToken && storedAccessToken !== config.headers.access_token) {
            config.headers.Authorization = storedAccessToken
        }

        return config
    },

    (error) => {
        console.log('interceptor error: ', error)
        return Promise.reject(error)
    }
)

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config
        if (error.response?.status !== 401 || originalRequest._retry) {
            return Promise.reject(error)
        }

        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject })
            }).then((token) => {
                originalRequest.headers.Authorization = token
                return api(originalRequest)
            })
        }

        originalRequest._retry = true
        isRefreshing = true

        try {
            const newToken = await refreshAccessToken()
            processQueue(null, newToken)
            originalRequest.headers.Authorization = newToken
            return api(originalRequest)
        } catch (refreshError) {
            processQueue(refreshError, null)
            window.localStorage.clear()
            window.sessionStorage.clear()
            window.location.href = getLoginUrl()
            return Promise.reject(refreshError)
        } finally {
            isRefreshing = false
        }
    }
)

export const apiDownload = (url: string, data: Record<string, unknown>) => {
    return api.post(url, data, { responseType: 'blob' })
}

export const apiUpload = (url: string, formData: FormData) => {
    return api.post(url, formData, { headers: { 'Content-Type': undefined } })
}

export default api
